import React, { useEffect, useMemo, useState } from 'react';
import {
  Typography, Card, Button, Space, Spin, Alert, message, Form,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  useGetDealQuery,
  useUpdateDealMutation,
  useListMerchantsQuery,
  useListCategoriesQuery,
} from './dealsApi';
import DealForm, { dealToFormValues, formValuesToPayload } from './DealForm';

const { Title, Text } = Typography;

function canEditDeal(user, deal) {
  if (!user || !deal) return false;
  if (['admin', 'moderator'].includes(user.role)) return true;
  return deal.submittedByPublicId && user.id === deal.submittedByPublicId;
}

export default function DealEditPage() {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [form] = Form.useForm();
  const [imageUrl, setImageUrl] = useState(null);

  const { data: deal, isLoading, error } = useGetDealQuery(publicId);
  const { data: merchantsData } = useListMerchantsQuery();
  const { data: categoriesData } = useListCategoriesQuery();
  const [updateDeal, { isLoading: saving }] = useUpdateDealMutation();

  const allowed = useMemo(() => canEditDeal(user, deal), [user, deal]);

  useEffect(() => {
    if (!deal) return;
    form.setFieldsValue(dealToFormValues(deal));
    const cover = deal.images?.[0]?.url || deal.primaryImageUrl || null;
    setImageUrl(cover);
  }, [deal, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = formValuesToPayload(values, imageUrl, { clearImagesWhenEmpty: true });
      await updateDeal({ publicId, ...payload }).unwrap();
      message.success('Deal updated');
      navigate(`/deals/${publicId}`);
    } catch (err) {
      if (err?.errorFields) return;
      const status = err?.status || err?.data?.status;
      if (status === 403) {
        message.error(err?.data?.title || 'You are not authorized to edit this deal');
      } else {
        message.error(err?.data?.title || 'Failed to update deal');
      }
    }
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '120px 0' }}>
        <Spin size="large" tip="Loading deal…" />
      </div>
    );
  }

  if (error || !deal) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Title level={4}>Deal not found</Title>
        <Button onClick={() => navigate('/')} size="large" style={{ borderRadius: 8, marginTop: 16 }}>
          Browse Deals
        </Button>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Sign in required"
        description="You must be signed in to edit a deal."
        action={<Button type="primary" onClick={() => navigate('/login')}>Sign in</Button>}
        style={{ marginTop: 40 }}
      />
    );
  }

  if (!allowed) {
    return (
      <Alert
        type="error"
        showIcon
        message="Not authorized"
        description="Only the deal owner, moderators, or admins can edit this deal."
        action={<Button onClick={() => navigate(`/deals/${publicId}`)}>Back to deal</Button>}
        style={{ marginTop: 40 }}
      />
    );
  }

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(`/deals/${publicId}`)}
        style={{ marginBottom: 16, color: 'var(--df-text-secondary)', padding: '0 4px' }}
      >
        Back to deal
      </Button>

      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0, fontWeight: 700, color: '#0f172a' }}>
          Edit Deal
        </Title>
        <Text type="secondary" style={{ fontSize: 14 }}>
          Update pricing, link, merchant, and details. Changes save immediately.
        </Text>
      </div>

      <Card style={{ borderRadius: 12, border: '1px solid #e2e8f0' }}>
        <DealForm
          form={form}
          merchants={merchantsData?.items || []}
          categories={categoriesData?.items || []}
          initialImageUrl={imageUrl}
          onImageChange={setImageUrl}
        />
      </Card>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
        <Button onClick={() => navigate(`/deals/${publicId}`)} size="large" style={{ borderRadius: 8 }}>
          Cancel
        </Button>
        <Space>
          <Button
            type="primary"
            size="large"
            loading={saving}
            onClick={handleSave}
            style={{ borderRadius: 8, fontWeight: 600, padding: '8px 32px' }}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </Space>
      </div>
    </div>
  );
}
